import {
  AreaAllUnits,
  MassAndWeightAllUnits,
  VolumeAllUnits,
  getCorrectUnitName
} from './unitsAbbreviation';
import { SERVICE_UNITS } from './unitConversionHelpers';

export type MeasurementFamily = 'area' | 'mass' | 'volume' | 'time' | 'unknown';

export type UnitCompatibilityVerdict =
  | 'ok'
  | 'withdrawn-unit'
  | 'density-required'
  | 'incompatible';

export interface UnitCompatibilityInput {
  type?: string | null;
  standardUnit?: string | null;
  averagePricePerUnitUnit?: string | null;
  density?: number | string | null;
}

const asFamilyList = (units: readonly unknown[]): string[] =>
  units.filter((unit): unit is string => typeof unit === 'string');

const AREA = asFamilyList(AreaAllUnits);
const MASS = asFamilyList(MassAndWeightAllUnits);
const VOLUME = asFamilyList(VolumeAllUnits);
const TIME = asFamilyList(SERVICE_UNITS);

export const getMeasurementFamily = (
  unit?: string | null
): MeasurementFamily => {
  if (!unit) return 'unknown';

  // The unit lists and the family arrays disagree on spelling ('acre inch' vs
  // 'acreinch', 'lbs' vs 'pound'), so membership only lines up after this.
  const name = getCorrectUnitName(unit);

  if (AREA.includes(name)) return 'area';
  if (MASS.includes(name)) return 'mass';
  if (VOLUME.includes(name)) return 'volume';
  if (TIME.includes(name)) return 'time';
  return 'unknown';
};

const hasUsableDensity = (density?: number | string | null): boolean => {
  if (density === null || density === undefined || density === '') return false;
  const parsed = Number(density);
  return Number.isFinite(parsed) && parsed > 0;
};

const isMassVolumePair = (
  first: MeasurementFamily,
  second: MeasurementFamily
): boolean =>
  (first === 'mass' && second === 'volume') ||
  (first === 'volume' && second === 'mass');

export const getUnitCompatibilityVerdict = (
  values: UnitCompatibilityInput,
  withdrawnUnits: readonly string[] = []
): UnitCompatibilityVerdict => {
  const { type, standardUnit, averagePricePerUnitUnit, density } = values;

  // A withdrawn unit blocks the save whether or not the pair would convert, so
  // this has to settle before any family comparison runs.
  if (type === 'FERTILIZER') {
    const isWithdrawn = (unit?: string | null) =>
      !!unit && withdrawnUnits.includes(unit);
    if (isWithdrawn(standardUnit) || isWithdrawn(averagePricePerUnitUnit)) {
      return 'withdrawn-unit';
    }
  }

  // An untouched list price unit mirrors the standard unit; treating it as
  // absent rather than mismatched keeps that case out of the error paths.
  const priceUnit = averagePricePerUnitUnit || standardUnit;
  if (!standardUnit || !priceUnit || standardUnit === priceUnit) return 'ok';

  const standardFamily = getMeasurementFamily(standardUnit);
  const priceFamily = getMeasurementFamily(priceUnit);

  // Refusing a combination the server would have accepted leaves the user with
  // no way forward, so anything unrecognised is allowed through.
  if (standardFamily === 'unknown' || priceFamily === 'unknown') return 'ok';

  if (standardFamily === priceFamily) return 'ok';

  if (isMassVolumePair(standardFamily, priceFamily)) {
    return hasUsableDensity(density) ? 'ok' : 'density-required';
  }

  return 'incompatible';
};
